import React, { useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import PageHeader from 'components/PageHeader';
import { PageContent } from 'components/layout/PageContent';
import userService from 'services/userService';
import { warmupAiAskTemplate } from 'services/aiAgentService';
import FieldPanel from './FieldPanel';
import EditorPanel from './EditorPanel';
import TemplateEditorFloatingDock from './TemplateEditorFloatingDock';
import TemplateNeoCoinsBar from './TemplateNeoCoinsBar';
import { useTemplateEditor } from '../context/TemplateEditorContext';
import useUnsavedChangesGuard from '../../../hooks/useUnsavedChangesGuard';

function pickNeoCoinsFromUser(user) {
  if (!user || typeof user !== 'object') return null;
  const raw = user.neo_coins ?? user.neoCoins ?? user.neo_coin ?? user.neoCoin;
  if (raw == null || raw === '') return null;
  const n = Number(raw);
  return Number.isFinite(n) ? Math.trunc(n) : String(raw);
}

const TemplateEditorLayout = ({
  pageTitle,
  title,
  subtitle,
}) => {
  const { isEditorDirty } = useTemplateEditor();
  useUnsavedChangesGuard(isEditorDirty);

  const [neoCoins, setNeoCoins] = useState(null);

  const applyNeoCoinBalanceFromAi = useCallback((raw) => {
    if (raw === undefined || raw === null) return;
    if (typeof raw === 'string' && raw.trim() === '') return;
    const n = Number(raw);
    setNeoCoins(Number.isFinite(n) ? Math.trunc(n) : String(raw).trim());
  }, []);

  useEffect(() => {
    warmupAiAskTemplate();
    let cancelled = false;
    (async () => {
      const result = await userService.getUserInfo();
      if (cancelled) return;
      if (!result?.success || !result.data) {
        setNeoCoins('—');
        return;
      }
      const picked = pickNeoCoinsFromUser(result.data);
      setNeoCoins(picked == null ? 0 : picked);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <PageContent className="flex flex-col min-h-[calc(100vh-4rem)] max-w-[100rem] w-full min-w-0 overflow-x-hidden box-border pb-[max(5.5rem,calc(env(safe-area-inset-bottom,0px)+4.5rem))] md:pb-0">
      <Helmet>
        <title>{pageTitle}</title>
      </Helmet>

      <PageHeader
        title={title}
        subtitle={subtitle}
        icon="LayoutTemplate"
        className="max-sm:mb-4"
      />

      <div
        className="sticky top-0 z-[60] mb-4 sm:mb-6 rounded-lg border border-border py-3 px-2 sm:px-3 md:-mx-6 md:px-6 lg:-mx-8 lg:px-8 md:rounded-none md:border-x-0 md:border-t-0 bg-background/95 backdrop-blur-md shadow-sm supports-[backdrop-filter]:bg-background/80"
        aria-label="Saldo Neo Coins"
      >
        <TemplateNeoCoinsBar neoCoins={neoCoins} />
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-4 lg:flex-row lg:gap-6">
        <div className="w-full shrink-0 lg:w-[30%] lg:min-w-[280px] lg:max-w-[420px] min-h-[320px] lg:min-h-0 lg:h-[calc(100vh-14rem)]">
          <FieldPanel />
        </div>

        <div className="min-h-0 flex-1 lg:h-[calc(100vh-14rem)]">
          <EditorPanel />
        </div>
      </div>

      <TemplateEditorFloatingDock onNeoCoinBalanceUpdate={applyNeoCoinBalanceFromAi} />
    </PageContent>
  );
};

export default TemplateEditorLayout;
